import { computed, inject, signal, Signal } from '@angular/core';
import { AssetAnalysisItem } from './api';
import { AssetsStore } from '../stores/assets.store';
import { AuthStore } from '../stores/auth.store';
import { ThemeService } from './theme';
import { ForexService } from './forex';
import { findV3AnalysisSignal, v3GuestMode, guestFallbackPotential } from './v3-guest-mode';
import { ConsensusHistoryEntry } from '../price-target-chart/price-line-chart';
import {
  getAnalystsOverview,
  getAnalystRatings,
  getAnalystsChartColors,
  calcGaugeValue,
  calcSellGaugeValue,
  consensusLabel,
  computeUpsidePercent,
  AnalystRating,
} from '../analysts-data';

export interface AnalystThesis {
  title: string;
  text: string;
}

export type RatingCategory = 'buy' | 'sell' | 'hold';

// Fixed left-to-right tab order — not re-sorted by count (only the default *selection* is
// picked by count, see activeRatingTab below).
export const RATING_TAB_ORDER: RatingCategory[] = ['buy', 'sell', 'hold'];

const RATING_TAB_LABELS: Record<RatingCategory, string> = {
  buy: 'Positiv',
  sell: 'Negativ',
  hold: 'Hold',
};

export interface AnalystsAnalysisData {
  isFinnet: Signal<boolean>;
  isGuestMode: Signal<boolean>;
  analystsAnalysis: Signal<AssetAnalysisItem | null>;
  hasContent: Signal<boolean>;
  rating: Signal<string>;
  ratingCategory: Signal<RatingCategory>;
  isPositive: Signal<boolean>;
  buyCount: Signal<number>;
  holdCount: Signal<number>;
  sellCount: Signal<number>;
  gaugeValue: Signal<number>;
  sellGaugeValue: Signal<number>;
  avgTarget: Signal<number | null>;
  highTarget: Signal<number | null>;
  lowTarget: Signal<number | null>;
  targetCurrency: Signal<string>;
  avgTargetEuro: Signal<number | null>;
  potential: Signal<number | null>;
  targetIsPositive: Signal<boolean>;
  analystCount: Signal<number | null>;
  consensusHistory: Signal<ConsensusHistoryEntry[] | null>;
  isZeroChartData: Signal<boolean>;
  chartPrimaryColor: Signal<string>;
  chartTargetLabelColor: Signal<string | undefined>;
  chartTargetPositiveColor: Signal<string>;
  chartTargetNegativeColor: Signal<string>;
  ratings: Signal<AnalystRating[]>;
  hasAnyRatings: Signal<boolean>;
  ratingsByCategory: Signal<Record<RatingCategory, AnalystRating[]>>;
  availableRatingTabs: Signal<RatingCategory[]>;
  activeRatingTab: Signal<RatingCategory | null>;
  selectRatingTab: (category: RatingCategory) => void;
  ratingTabLabel: (category: RatingCategory) => string;
  ratingTabCount: (category: RatingCategory) => number;
  ratingKey: (r: AnalystRating) => string;
  showPositiveTheses: Signal<boolean>;
  theses: Signal<AnalystThesis[]>;
  consensusTheses: Signal<AnalystThesis[]>;
}

// Analysts computeds shared by V3AnalystsDesktopContent, V3AnalystsAnalysis and
// V3AnalystsOverview. Caller-specific bits (isDetail sizing, pagination, slicing) stay local.
//
// isFinnet: pass the caller's own signal to avoid a redundant computed(); omitted, one is derived.
export function analystsAnalysisData(
  currentPrice: Signal<number | null>,
  isFinnet?: Signal<boolean>,
): AnalystsAnalysisData {
  const assetsStore = inject(AssetsStore);
  const authStore = inject(AuthStore);
  const themeService = inject(ThemeService);
  const forexService = inject(ForexService);

  const resolvedIsFinnet = isFinnet ?? computed(() => themeService.isFinnet());
  const isGuestMode = v3GuestMode(themeService, authStore);

  const analystsAnalysis = findV3AnalysisSignal(
    assetsStore.v2Data,
    'analysts_analysis',
    isGuestMode,
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const overview = computed<Record<string, any> | null>(() =>
    getAnalystsOverview(analystsAnalysis()),
  );

  const hasContent = computed(() => !!overview());

  const rating = computed(() => consensusLabel(overview()?.['consensus_label']));

  const ratingCategory = computed<RatingCategory>(() => {
    const raw = overview()?.['consensus_label'] as string | undefined;
    if (raw?.includes('BUY')) return 'buy';
    if (raw?.includes('SELL')) return 'sell';
    return 'hold';
  });

  // Hold falls in with buy — the product spec only distinguishes buy/sell.
  const isPositive = computed(() => ratingCategory() !== 'sell');

  const buyCount = computed(() => overview()?.['rating_counts']?.['buy'] ?? 0);
  const holdCount = computed(() => overview()?.['rating_counts']?.['hold'] ?? 0);
  const sellCount = computed(() => overview()?.['rating_counts']?.['sell'] ?? 0);

  const gaugeValue = computed(() => calcGaugeValue(buyCount(), holdCount(), sellCount()));
  const sellGaugeValue = computed(() => calcSellGaugeValue(buyCount(), holdCount(), sellCount()));

  function parseTargetField(key: 'consensus_target' | 'high_target' | 'low_target'): number | null {
    const raw = overview()?.['price_target_summary']?.[key];
    if (raw == null) return null;
    const value = Number.parseFloat(raw as string);
    return Number.isFinite(value) ? value : null;
  }

  const avgTarget = computed(() => parseTargetField('consensus_target'));
  const highTarget = computed(() => parseTargetField('high_target'));
  const lowTarget = computed(() => parseTargetField('low_target'));

  const targetCurrency = computed<string>(
    () => overview()?.['price_target_summary']?.['currency'] ?? '',
  );

  // Pre-converted for the count-up span, mirroring ForexPipe's '–' guards; each template
  // re-adds the ' €' suffix statically.
  const avgTargetEuro = computed(() => {
    const target = avgTarget();
    const currency = targetCurrency();
    if (target == null || !currency || !forexService.loaded()) return null;
    return forexService.toEuro(target, currency);
  });

  const potential = computed(() =>
    guestFallbackPotential(
      computeUpsidePercent(currentPrice(), avgTarget(), targetCurrency(), forexService),
      isGuestMode(),
    ),
  );

  const targetIsPositive = computed(() => (potential() ?? 0) >= 0);

  const analystCount = computed<number | null>(() => {
    const raw = overview()?.['analyst_count'];
    return typeof raw === 'number' ? raw : null;
  });

  const consensusHistory = computed<ConsensusHistoryEntry[] | null>(() => {
    const raw = analystsAnalysis()?.content?.['consensus_history'];
    return Array.isArray(raw) ? (raw as ConsensusHistoryEntry[]) : null;
  });

  const isZeroChartData = computed(
    () => analystsAnalysis()?.content?.['chart_data_provider'] === 'finanzen.net zero',
  );

  // Shared so the desktop chart and the mobile Kursziele chart can't drift apart.
  const chartColors = computed(() =>
    getAnalystsChartColors(resolvedIsFinnet(), themeService.isDark()),
  );
  const chartPrimaryColor = computed(() => chartColors().primaryColor);
  const chartTargetLabelColor = computed(() => chartColors().targetLabelColor);
  const chartTargetPositiveColor = computed(() => chartColors().targetPositiveColor);
  const chartTargetNegativeColor = computed(() => chartColors().targetNegativeColor);

  // Every rating, newest first.
  const ratings = computed<AnalystRating[]>(() => getAnalystRatings(analystsAnalysis()));
  const hasAnyRatings = computed(() => ratings().length > 0);

  const ratingsByCategory = computed<Record<RatingCategory, AnalystRating[]>>(() => {
    const all = ratings();
    return {
      buy: all.filter((r) => r.rating === 'Buy'),
      sell: all.filter((r) => r.rating === 'Sell'),
      hold: all.filter((r) => r.rating === 'Hold'),
    };
  });

  // "Negativ" is always shown once there's at least one rating of any category — even with zero
  // Sell ratings — so negative theses (showPositiveTheses below) stay reachable. Buy/Hold keep
  // the "hide if empty" rule.
  const availableRatingTabs = computed<RatingCategory[]>(() => {
    const byCategory = ratingsByCategory();
    const hasAny = hasAnyRatings();
    return RATING_TAB_ORDER.filter(
      (category) => byCategory[category].length > 0 || (category === 'sell' && hasAny),
    );
  });

  // Defaults to the category with the most ratings; ties keep the fixed tab order above.
  const defaultRatingTab = computed<RatingCategory | null>(() => {
    const byCategory = ratingsByCategory();
    const available = availableRatingTabs();
    if (available.length === 0) return null;
    return available.reduce(
      (best, current) => (byCategory[current].length > byCategory[best].length ? current : best),
      available[0],
    );
  });

  const selectedRatingTab = signal<RatingCategory | null>(null);

  // Falls back to the default whenever nothing's been explicitly clicked yet, or the previously
  // selected category no longer has any ratings.
  const activeRatingTab = computed<RatingCategory | null>(() => {
    const selected = selectedRatingTab();
    if (selected && availableRatingTabs().includes(selected)) return selected;
    return defaultRatingTab();
  });

  function selectRatingTab(category: RatingCategory): void {
    selectedRatingTab.set(category);
  }

  function ratingTabLabel(category: RatingCategory): string {
    return RATING_TAB_LABELS[category];
  }

  function ratingTabCount(category: RatingCategory): number {
    return ratingsByCategory()[category].length;
  }

  function ratingKey(r: AnalystRating): string {
    return `${r.name}|${r.firm}|${r.date}`;
  }

  function mapTheses(raw: unknown): AnalystThesis[] {
    if (!Array.isArray(raw)) return [];
    return (raw as { restricted?: boolean; title?: string; body?: string }[])
      .filter((t) => !t.restricted)
      .map((t) => ({ title: t.title ?? '', text: t.body ?? '' }));
  }

  // Follows the active rating-category tab, not the overall consensus — falls back to the
  // overall category only when there are no individual ratings at all. Hold tracks Positiv.
  const showPositiveTheses = computed(() => (activeRatingTab() ?? ratingCategory()) !== 'sell');

  function thesesFor(positive: boolean): AnalystThesis[] {
    const content = analystsAnalysis()?.content;
    return mapTheses(content?.[positive ? 'positive_theses' : 'negative_theses']);
  }

  const theses = computed<AnalystThesis[]>(() => thesesFor(showPositiveTheses()));

  // For V3AnalystsOverview, which has no tabs — follows the consensus, not activeRatingTab.
  const consensusTheses = computed<AnalystThesis[]>(() => thesesFor(isPositive()));

  return {
    isFinnet: resolvedIsFinnet,
    isGuestMode,
    analystsAnalysis,
    hasContent,
    rating,
    ratingCategory,
    isPositive,
    buyCount,
    holdCount,
    sellCount,
    gaugeValue,
    sellGaugeValue,
    avgTarget,
    highTarget,
    lowTarget,
    targetCurrency,
    avgTargetEuro,
    potential,
    targetIsPositive,
    analystCount,
    consensusHistory,
    isZeroChartData,
    chartPrimaryColor,
    chartTargetLabelColor,
    chartTargetPositiveColor,
    chartTargetNegativeColor,
    ratings,
    hasAnyRatings,
    ratingsByCategory,
    availableRatingTabs,
    activeRatingTab,
    selectRatingTab,
    ratingTabLabel,
    ratingTabCount,
    ratingKey,
    showPositiveTheses,
    theses,
    consensusTheses,
  };
}
